import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Bell, ArrowLeft, Loader2, Check } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface WaitlistFormProps {
  restaurantId: string;
  date: Date;
  partySize: number;
  desiredTime?: string | null;
  onBack: () => void;
}

const WaitlistForm: React.FC<WaitlistFormProps> = ({ restaurantId, date, partySize, desiredTime, onBack }) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async () => {
    if (!name.trim() || !email.trim()) return;
    setSubmitting(true);
    try {
      const { error } = await supabase.functions.invoke('add-to-waitlist', {
        body: {
          restaurantId,
          desiredDate: date.toISOString().split('T')[0],
          desiredTime: desiredTime || null,
          partySize,
          guestName: name.trim(),
          guestEmail: email.trim().toLowerCase(),
        },
      });
      if (error) throw error;
      setSubmitted(true);
    } catch (err: any) {
      toast({ title: 'Erreur', description: err.message || 'Impossible de vous inscrire', variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="text-center py-6 space-y-3">
        <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center mx-auto">
          <Check className="h-6 w-6 text-emerald-600" />
        </div>
        <p className="font-semibold text-sm">Alerte activée !</p>
        <p className="text-xs text-muted-foreground">Vous serez prévenu(e) par email si une place se libère.</p>
      </div>
    );
  }

  return (
    <div className="border rounded-xl p-4 space-y-3 bg-muted/30">
      <div className="text-center space-y-1">
        <p className="text-sm">😔 Les créneaux sont complets</p>
        <p className="text-xs text-muted-foreground flex items-center justify-center gap-1">
          <Bell className="h-3.5 w-3.5" /> Me prévenir si une place se libère
        </p>
      </div>
      <Input placeholder="Prénom" value={name} onChange={e => setName(e.target.value)} className="h-9 text-sm" />
      <Input type="email" placeholder="Email" value={email} onChange={e => setEmail(e.target.value)} className="h-9 text-sm" />
      <Button onClick={handleSubmit} disabled={submitting || !name.trim() || !email.trim()} className="w-full gap-2">
        {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Bell className="h-4 w-4" />}
        Activer l'alerte
      </Button>
      <Button variant="ghost" size="sm" className="w-full gap-1" onClick={onBack}>
        <ArrowLeft className="h-3.5 w-3.5" /> Essayer une autre date
      </Button>
    </div>
  );
};

export default WaitlistForm;
